import React, { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useAdminAuth } from './contexts/ConditionalAdminAuthProvider';
import ErrorBoundary from './components/ErrorBoundary';

// Public SEO pages — synchronous imports for reliable pre-render
import LandingPage from './pages/LandingPage';
import GuidesIndexPage from './pages/GuidesIndexPage';
import GuideArticlePage from './pages/GuideArticlePage';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import MatomoAnalytics from './components/Analytics/MatomoAnalytics';

// Private / heavy routes — lazy loaded to reduce public bundle size
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const MagicLoginPage = lazy(() => import('./pages/MagicLoginPage'));
const MagicLinkPage = lazy(() => import('./pages/MagicLinkPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const LegalMentionsLegalesPage = lazy(() => import('./pages/LegalMentionsLegalesPage'));
const LegalCgvPage = lazy(() => import('./pages/LegalCgvPage'));
const LegalConfidentialitePage = lazy(() => import('./pages/LegalConfidentialitePage'));
const LegalCguB2BPage = lazy(() => import('./pages/LegalCguB2BPage'));
const LegalCguEndUsersPage = lazy(() => import('./pages/LegalCguEndUsersPage'));
const GuestJoin = lazy(() => import('./pages/GuestJoin'));

function App() {
  const { isAuthenticated } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();

  return (
    <ErrorBoundary>
      <MatomoAnalytics>
        <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />} />
        <Route path="/register" element={!isAuthenticated ? <RegisterPage /> : <Navigate to="/dashboard" />} />
        <Route path="/magic-login" element={!isAuthenticated ? <MagicLoginPage /> : <Navigate to="/dashboard" />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/magic-link/:token" element={<MagicLinkPage />} />
        <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        
        {/* Guest invitation route - public */}
        <Route path="/join/:token" element={<GuestJoin />} />
        
        <Route path="/dashboard/*" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="/admin/login" element={!isAdminAuthenticated ? <AdminLoginPage /> : <Navigate to="/admin/dashboard" />} />
        <Route path="/admin/dashboard/*" element={
          <ProtectedAdminRoute>
            <AdminDashboardPage />
          </ProtectedAdminRoute>
        } />
        
        {/* Pages légales */}
        <Route path="/mentions-legales" element={<LegalMentionsLegalesPage />} />
        <Route path="/cgv" element={<LegalCgvPage />} />
        <Route path="/confidentialite" element={<LegalConfidentialitePage />} />
        <Route path="/cgu-b2b" element={<LegalCguB2BPage />} />
        <Route path="/cgu-utilisateurs" element={<LegalCguEndUsersPage />} />

        {/* Guides SEO */}
        <Route path="/guides" element={<GuidesIndexPage />} />
        <Route path="/guides/:slugPath" element={<GuideArticlePage />} />

        {/* Pre-render target for static 404.html (build/scripts/prerender.js) */}
        <Route path="/__prerender-not-found__" element={<NotFoundPage />} />
        
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </MatomoAnalytics>
    </ErrorBoundary>
  );
}

export default App;
